import type { AudioEvent } from '../lib/playbackReport.ts';
import { stripTags } from '../lib/text.ts';
import { audioUrl } from './audioIds.ts';

export interface Fallback {
  text: string;
  lang: 'en' | 'pl';
  slow?: boolean;
}

/** Jeden klocek wypowiedzi złożonej z kilku nagrań (patrz `Player.playAll`). */
export interface PlayPart {
  id: string;
  fallback: Fallback;
}

export interface Player {
  /** Odtwarza nagranie <id>.mp3; gdy go brak, czyta tekst głosem przeglądarki. Zawsze się kończy (resolve). */
  play(id: string, fallback: Fallback): Promise<void>;
  /**
   * Odtwarza kilka nagrań jedno po drugim, z krótką przerwą (`gapMs`) — jedna wypowiedź złożona z klocków. Każdy klocek gra tak jak `play`
   * (brak pliku = głos przeglądarki). `stop()` albo inne `play` przerywa całość: kolejne klocki już nie zagrają.
   */
  playAll(parts: readonly PlayPart[], gapMs?: number): Promise<void>;
  stop(): void;
  /** Dziennik ostatnich zdarzeń odtwarzania (start, koniec, przerwanie z powodem) — do diagnostyki „kto przerwał dźwięk”. */
  log?(): readonly AudioEvent[];
  /** Wywołaj w pierwszym geście użytkownika (tap) — odblokowuje audio na iOS/Safari. */
  unlock(): void;
}

const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

/**
 * `source(id)` mówi, skąd wziąć nagranie: tekst = adres pliku (np. z R2), `null` = nagrania nie ma (od razu głos przeglądarki),
 * `undefined` = domyślnie plik wbudowany w aplikację (audio/<id>.mp3).
 */
export type AudioSource = (id: string) => string | null | undefined;

export function createPlayer(base = './', source: AudioSource = () => undefined): Player {
  const el = typeof Audio !== 'undefined' ? new Audio() : null;
  const missing = new Set<string>();
  let token = 0;
  let cancelCurrent: (() => void) | null = null;
  const events: AudioEvent[] = [];
  let current: string | null = null; // nagranie, które właśnie gra (do zapisu, że je przerwano)
  const round = (n: number) => Math.round(n * 100) / 100;
  const note = (what: AudioEvent['what'], id: string, why?: string) => {
    const e: AudioEvent = { at: Date.now(), what, id, ...(why ? { why } : {}) };
    if ((what === 'ended' || what === 'cut') && el) {
      e.cur = round(el.currentTime);
      if (Number.isFinite(el.duration)) e.dur = round(el.duration);
    }
    events.push(e);
    if (events.length > 40) events.shift();
  };
  /** Przerwanie grającego nagrania: zapisujemy, kto je przerwał i w którym miejscu. */
  const noteCut = (why: string) => {
    if (current === null) return;
    note('cut', current, why);
    current = null;
  };

  function speak(f: Fallback, mine: number): Promise<void> {
    return new Promise((resolve) => {
      const synth = typeof speechSynthesis !== 'undefined' ? speechSynthesis : null;
      const text = stripTags(f.text);
      if (!synth || !text || mine !== token) return resolve();
      let done = false;
      const end = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve();
      };
      // niektóre przeglądarki nigdy nie wywołają onend (brak głosów) — zabezpieczenie czasowe
      const timer = setTimeout(end, Math.max(2500, text.length * 120));
      cancelCurrent = end;
      try {
        const u = new SpeechSynthesisUtterance(text);
        u.lang = f.lang === 'pl' ? 'pl-PL' : 'en-US';
        const voices = synth.getVoices();
        const voice = voices.find((v) => v.lang.replace('_', '-').toLowerCase() === u.lang.toLowerCase()) ?? voices.find((v) => v.lang.toLowerCase().startsWith(f.lang));
        if (voice) u.voice = voice;
        u.rate = f.slow ? 0.65 : 0.9;
        u.onend = end;
        u.onerror = end;
        synth.cancel();
        synth.speak(u);
      } catch {
        end();
      }
    });
  }

  function playFile(url: string, mine: number): Promise<'ok' | 'missing' | 'blocked'> {
    return new Promise((resolve) => {
      if (!el || mine !== token) return resolve('missing');
      let done = false;
      const end = (r: 'ok' | 'missing' | 'blocked') => {
        if (done) return;
        done = true;
        el.onended = el.onerror = null;
        resolve(r);
      };
      cancelCurrent = () => end('ok');
      el.onended = () => {
        if (current !== null) note('ended', current);
        current = null;
        end('ok');
      };
      el.onerror = () => {
        if (current !== null) note('error', current);
        current = null;
        end('missing');
      };
      el.src = url;
      el.play().catch((e: unknown) => {
        const blocked = (e as { name?: string })?.name === 'NotAllowedError';
        if (blocked && current !== null) note('blocked', current);
        end(blocked ? 'blocked' : 'missing');
      });
    });
  }

  /** Gra jedno nagranie (albo głos zapasowy). Zwraca numer „własnego” odtwarzania: jeśli `token` jest już inny, ktoś je przerwał. */
  async function run(id: string, fallback: Fallback): Promise<number> {
    const mine = ++token;
    noteCut(`zastąpione przez ${id}`);
    cancelCurrent?.();
    cancelCurrent = null;
    el?.pause();
    if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
    const chosen = source(id);
    const url = chosen === null ? null : (chosen ?? audioUrl(id, base));
    if (el && url !== null && !missing.has(url)) {
      current = id;
      note('play', id);
      const r = await playFile(url, mine);
      if (r === 'ok' || r === 'blocked' || mine !== token) return mine;
      missing.add(url); // brak pliku — przy następnym razie od razu głos zapasowy (a nowy adres po zmianie zdania dostanie nową szansę)
      current = null;
    }
    if (mine !== token) return mine;
    note('fallback', id, url === null ? 'to zdanie nie ma jeszcze nagrania — głos przeglądarki' : 'brak pliku mp3 — głos przeglądarki');
    await speak(fallback, mine);
    return mine;
  }

  return {
    async play(id, fallback) {
      await run(id, fallback);
    },
    async playAll(parts, gapMs = 140) {
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i] as PlayPart;
        const mine = await run(part.id, part.fallback);
        if (mine !== token) return; // przerwane przez stop() albo inne nagranie
        if (i < parts.length - 1 && gapMs > 0) {
          await new Promise<void>((r) => setTimeout(r, gapMs));
          if (mine !== token) return;
        }
      }
    },
    log: () => events,
    stop() {
      noteCut('stop() — zmiana zadania, „Mów” albo zamknięcie');
      token++;
      cancelCurrent?.();
      cancelCurrent = null;
      el?.pause();
      if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel();
    },
    unlock() {
      try {
        if (el) {
          el.src = SILENT_WAV;
          void el.play().catch(() => {});
        }
        if (typeof speechSynthesis !== 'undefined') speechSynthesis.speak(new SpeechSynthesisUtterance(''));
      } catch {
        /* ignore */
      }
    },
  };
}
